import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/cn';

interface TopbarProps {
  title?: React.ReactNode;
  breadcrumbs?: { label: string; href?: string }[];
  onOpenSidebar: () => void;
  children?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}

// Path slugs that should render fully uppercase in the auto-title (e.g. /vps → VPS).
const UPPERCASE_SLUGS = new Set(['vps', 'ai', 'api', 'sso', 'iam']);

export function Topbar({ title, breadcrumbs, onOpenSidebar, children, right, className }: TopbarProps) {
  const location = useLocation();
  const lastSlug = location.pathname.split('/').filter(Boolean).pop() || 'Dashboard';
  const derivedTitle = UPPERCASE_SLUGS.has(lastSlug.toLowerCase())
    ? lastSlug.toUpperCase()
    : lastSlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const autoTitle = breadcrumbs?.[breadcrumbs.length - 1]?.label || title || derivedTitle;

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-6',
        className
      )}
    >
      <button
        type="button"
        className="-ml-1 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
        onClick={onOpenSidebar}
        aria-label="Open sidebar"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
            {breadcrumbs.map((crumb, i) => {
              const last = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                  {last || !crumb.href ? (
                    <span className={cn('truncate', last ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                      {crumb.label}
                    </span>
                  ) : (
                    <Link to={crumb.href} className="truncate text-muted-foreground hover:text-foreground">
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        ) : (
          <h1 className="truncate text-sm font-semibold text-foreground">{autoTitle}</h1>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        {children}
        {right}
      </div>
    </header>
  );
}
